// Admin — Threat Intelligence source management.
//
// Platform Admins add/enable/disable/remove the RSS/Atom feeds that power the
// Threat Intelligence feed and Insights. Up to 25 sources. Rendered inside the
// Integration settings tab.

import React, { useCallback, useEffect, useState } from "react";
import {
  FaRss,
  FaPlus,
  FaTrashAlt,
  FaToggleOn,
  FaToggleOff,
  FaPen,
  FaSave,
  FaTimes,
  FaShieldAlt,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { isPlatformAdmin } from "../../constants/roles";
import "./ThreatIntelSourcesSettings.css";

export default function ThreatIntelSourcesSettings() {
  const { token, user } = useAuth();
  const isAdmin = isPlatformAdmin(user?.role);

  const [sources, setSources] = useState([]);
  const [max, setMax] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Add form
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  // Inline edit + per-row busy
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [busyId, setBusyId] = useState(null);

  const authHeaders = useCallback(
    (extra = {}) => ({ Authorization: `Bearer ${token}`, ...extra }),
    [token]
  );

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await fetch("/api/threat-intel/admin/sources", { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setSources(json.sources || []);
      setMax(json.max || 25);
    } catch (e) {
      setError(e.message || "Failed to load sources");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError("");
    if (!newName.trim() || !newUrl.trim()) {
      setAddError("Name and feed URL are required.");
      return;
    }
    setAdding(true);
    try {
      const res = await fetch("/api/threat-intel/admin/sources", {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ name: newName.trim(), url: newUrl.trim() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setNewName("");
      setNewUrl("");
      await load();
    } catch (e) {
      setAddError(e.message || "Failed to add source");
    } finally {
      setAdding(false);
    }
  };

  const patch = async (id, body) => {
    setBusyId(id);
    setError("");
    try {
      const res = await fetch(`/api/threat-intel/admin/sources/${id}`, {
        method: "PUT",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      await load();
      return true;
    } catch (e) {
      setError(e.message || "Failed to update source");
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const handleToggle = (s) => patch(s.id, { enabled: !s.enabled });

  const startEdit = (s) => {
    setEditId(s.id);
    setEditName(s.name);
    setEditUrl(s.url);
  };
  const cancelEdit = () => {
    setEditId(null);
    setEditName("");
    setEditUrl("");
  };
  const saveEdit = async (id) => {
    if (!editName.trim() || !editUrl.trim()) return;
    const ok = await patch(id, { name: editName.trim(), url: editUrl.trim() });
    if (ok) cancelEdit();
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Remove "${s.name}"? It will no longer appear in the feed or insights.`)) return;
    setBusyId(s.id);
    setError("");
    try {
      const res = await fetch(`/api/threat-intel/admin/sources/${s.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      await load();
    } catch (e) {
      setError(e.message || "Failed to delete source");
    } finally {
      setBusyId(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="tis-forbidden">
        <FaShieldAlt />
        <p>This section is only available to Platform Admins.</p>
      </div>
    );
  }

  const enabledCount = sources.filter((s) => s.enabled).length;
  const atLimit = sources.length >= max;

  return (
    <section className="tis-section">
      <div className="tis-head">
        <div>
          <h2 className="tis-title">
            <FaRss /> Threat Intelligence Sources
          </h2>
          <p className="tis-desc">
            RSS / Atom feeds powering the Threat Intelligence feed and Insights. Add up to {max}{" "}
            sources; disabled ones are kept but skipped.
          </p>
        </div>
        <div className="tis-counter">
          <span className="tis-counter-num">{sources.length}</span>
          <span className="tis-counter-den">/ {max}</span>
          <span className="tis-counter-label">{enabledCount} enabled</span>
        </div>
      </div>

      {/* Add form */}
      <form className="tis-add" onSubmit={handleAdd}>
        <input
          className="tis-input tis-input-name"
          placeholder="Source name (e.g. Krebs on Security)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          disabled={atLimit || adding}
        />
        <input
          className="tis-input tis-input-url"
          placeholder="Feed URL (https://…/feed)"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          disabled={atLimit || adding}
        />
        <button type="submit" className="tis-add-btn" disabled={atLimit || adding}>
          <FaPlus /> {adding ? "Adding…" : "Add"}
        </button>
      </form>
      {atLimit && <p className="tis-limit-note">Maximum of {max} sources reached. Remove one to add another.</p>}
      {addError && <p className="tis-add-error">{addError}</p>}
      {error && <p className="tis-add-error">{error}</p>}

      {/* List */}
      {loading ? (
        <p className="tis-loading">Loading sources…</p>
      ) : sources.length === 0 ? (
        <p className="tis-empty">No sources configured. Add one above to start pulling threat intel.</p>
      ) : (
        <ul className="tis-list">
          {sources.map((s) => (
            <li key={s.id} className={`tis-row ${s.enabled ? "" : "is-off"}`}>
              {editId === s.id ? (
                <div className="tis-edit">
                  <input
                    className="tis-input tis-input-name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Name"
                  />
                  <input
                    className="tis-input tis-input-url"
                    value={editUrl}
                    onChange={(e) => setEditUrl(e.target.value)}
                    placeholder="Feed URL"
                  />
                  <div className="tis-row-actions">
                    <button
                      className="tis-icon-btn save"
                      onClick={() => saveEdit(s.id)}
                      disabled={busyId === s.id || !editName.trim() || !editUrl.trim()}
                      title="Save"
                    >
                      <FaSave />
                    </button>
                    <button className="tis-icon-btn" onClick={cancelEdit} title="Cancel">
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="tis-row-main">
                    <div className="tis-row-name">
                      {s.name}
                      {s.is_default && <span className="tis-badge">default</span>}
                    </div>
                    <a
                      className="tis-row-url"
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={s.url}
                    >
                      {s.url} <FaExternalLinkAlt />
                    </a>
                  </div>
                  <div className="tis-row-actions">
                    <button
                      className={`tis-toggle ${s.enabled ? "on" : "off"}`}
                      onClick={() => handleToggle(s)}
                      disabled={busyId === s.id}
                      title={s.enabled ? "Disable" : "Enable"}
                    >
                      {s.enabled ? <FaToggleOn /> : <FaToggleOff />}
                      <span>{s.enabled ? "Enabled" : "Disabled"}</span>
                    </button>
                    <button
                      className="tis-icon-btn"
                      onClick={() => startEdit(s)}
                      disabled={busyId === s.id}
                      title="Edit"
                    >
                      <FaPen />
                    </button>
                    <button
                      className="tis-icon-btn danger"
                      onClick={() => handleDelete(s)}
                      disabled={busyId === s.id}
                      title="Remove"
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
